import * as Sentry from "@sentry/nextjs";
import type {
  AvailablePaymentMethod,
  CartItem,
  CartResponse,
} from "@/networking/carts.types";
import { toPaymentAmount } from "@/lib/utils";

type IntervalUnit = "day" | "week" | "month" | "year";

type RecurringCartItem = Partial<
  Pick<CartItem, "subscription" | "subscription_only">
>;

type ApplePayAvailabilityMethod = Partial<
  Pick<
    AvailablePaymentMethod,
    | "id"
    | "type"
    | "apple_pay"
    | "apple_pay_recurring_supported"
    | "decrypted_apple_pay_via_paypal"
  >
>;

type PaypalApplePayMode = "decrypted" | "standard";

type MissingApplePayRecurringSupportDiagnostic = {
  key: string;
  data: {
    apple_pay_method_count: number;
    paypal_apple_pay_mode: PaypalApplePayMode;
    payment_method_types: string[];
  };
};

export function hasRecurringCartItems(
  items?: readonly RecurringCartItem[] | null,
): boolean {
  return (
    items?.some(
      (item) => item.subscription === true || item.subscription_only === true,
    ) === true
  );
}

/**
 * QA-only override for exercising recurring Apple Pay before backend capability
 * is enabled. Supported switches:
 *   - `?test_recurring=1`
 *   - `window.__testRecurring = true`
 *   - `localStorage["checkout:test_recurring"] = "1"`
 */
export function isApplePayRecurringTestOverrideEnabled(): boolean {
  if (typeof window === "undefined") return false;

  const qp = new URLSearchParams(window.location.search);
  if (qp.get("test_recurring") === "1") return true;

  if (
    (window as Window & { __testRecurring?: boolean }).__testRecurring === true
  )
    return true;

  try {
    if (window.localStorage?.getItem("checkout:test_recurring") === "1")
      return true;
  } catch {
    // Safari private mode and blocked storage policies can throw here.
  }

  return false;
}

function isApplePayMethodAvailable(
  method: ApplePayAvailabilityMethod,
  paypalApplePayMode: PaypalApplePayMode,
): boolean {
  if (method.type === "credit_card") return method.apple_pay === true;
  if (method.type !== "paypal") return false;

  return paypalApplePayMode === "standard"
    ? method.apple_pay === true
    : method.decrypted_apple_pay_via_paypal === true;
}

function getAvailableApplePayMethods(
  methods: readonly ApplePayAvailabilityMethod[] | null | undefined,
  paypalApplePayMode: PaypalApplePayMode,
): ApplePayAvailabilityMethod[] {
  return (
    methods?.filter((method) =>
      isApplePayMethodAvailable(method, paypalApplePayMode),
    ) ?? []
  );
}

export function getMissingApplePayRecurringSupportDiagnostic({
  methods,
  hasRecurringItems,
  paypalApplePayMode = "decrypted",
  recurringTestOverride = false,
}: {
  methods?: readonly ApplePayAvailabilityMethod[] | null;
  hasRecurringItems: boolean;
  paypalApplePayMode?: PaypalApplePayMode;
  recurringTestOverride?: boolean;
}): MissingApplePayRecurringSupportDiagnostic | null {
  const applePayMethods = getAvailableApplePayMethods(
    methods,
    paypalApplePayMode,
  );
  if (applePayMethods.length === 0) return null;
  if (!hasRecurringItems) return null;
  if (recurringTestOverride) return null;
  if (
    applePayMethods.some(
      (method) => method.apple_pay_recurring_supported === true,
    )
  )
    return null;
  if (
    applePayMethods.some(
      (method) => typeof method.apple_pay_recurring_supported === "boolean",
    )
  )
    return null;

  const paymentMethodTypes = applePayMethods
    .map((method) => method.type)
    .filter((type): type is string => typeof type === "string");
  const key = applePayMethods
    .map((method) => `${method.type ?? "unknown"}:${method.id ?? "unknown"}`)
    .join("|");

  return {
    key: `${paypalApplePayMode}:${key}`,
    data: {
      apple_pay_method_count: applePayMethods.length,
      paypal_apple_pay_mode: paypalApplePayMode,
      payment_method_types: paymentMethodTypes,
    },
  };
}

export function addMissingApplePayRecurringSupportBreadcrumb(
  diagnostic: MissingApplePayRecurringSupportDiagnostic,
): void {
  Sentry.addBreadcrumb({
    category: "checkout.apple_pay",
    level: "info",
    message:
      "Recurring Apple Pay hidden because payment methods are missing apple_pay_recurring_supported.",
    data: diagnostic.data,
  });
}

export function isApplePayAvailableForCart({
  methods,
  hasRecurringItems,
  paypalApplePayMode = "decrypted",
  recurringTestOverride = false,
}: {
  methods?: readonly ApplePayAvailabilityMethod[] | null;
  hasRecurringItems: boolean;
  paypalApplePayMode?: PaypalApplePayMode;
  recurringTestOverride?: boolean;
}): boolean {
  const applePayMethods = getAvailableApplePayMethods(
    methods,
    paypalApplePayMode,
  );
  if (applePayMethods.length === 0) return false;
  if (!hasRecurringItems) return true;
  if (recurringTestOverride) return true;

  if (
    applePayMethods.some(
      (method) => method.apple_pay_recurring_supported === true,
    )
  )
    return true;

  return false;
}

function toIsoDate(d: string | Date | null | undefined): Date | undefined {
  if (!d) return undefined;
  return d instanceof Date ? d : new Date(d);
}

function normalizeUnitCount(
  u: IntervalUnit,
  count: number,
): {
  unit: ApplePayJS.ApplePayRecurringPaymentDateUnit;
  normalizedCount: number;
} {
  const safeCount = Math.max(1, Number(count) || 1);
  switch (u) {
    case "week":
      // https://developer.apple.com/documentation/applepayontheweb/applepayrecurringpaymentdateunit
      // Week is not supported by ApplePay, so we convert it to days
      return { unit: "day", normalizedCount: safeCount * 7 };
    case "day":
      return { unit: "day", normalizedCount: safeCount };
    case "month":
      return { unit: "month", normalizedCount: safeCount };
    case "year":
    default:
      return { unit: "year", normalizedCount: safeCount };
  }
}

/**
 * Build ApplePayLineItem for recurring/trial rows.
 * `amount` must be a string like "9.99".
 */
export function buildRecurringLineItem(opts: {
  label: string;
  amount: string;
  startDate?: string | Date | null;
  intervalUnit: IntervalUnit;
  intervalCount: number;
  endDate?: string | Date | null; // omit for infinite
}): ApplePayJS.ApplePayLineItem {
  const { unit, normalizedCount } = normalizeUnitCount(
    opts.intervalUnit,
    opts.intervalCount,
  );

  const li: ApplePayJS.ApplePayLineItem = {
    type: "final",
    label: opts.label,
    amount: opts.amount,
    paymentTiming: "recurring",
    recurringPaymentStartDate: toIsoDate(opts.startDate),
    recurringPaymentIntervalUnit: unit,
    recurringPaymentIntervalCount: normalizedCount,
  };

  const end = toIsoDate(opts.endDate);
  if (end) li.recurringPaymentEndDate = end;

  return li;
}

export function buildRecurringPaymentRequestFromCart(
  cart: CartResponse["cart"],
  decimalPlaces: number,
) {
  // choose a subscription item
  const subItem =
    cart.items.find((i) => i.subscription || i.subscription_only) ??
    cart.items[0];

  // pick the *selected* plan first; fallback to first
  const chosenPlan =
    subItem?.subscription_plans?.find(
      (p) => p.id === subItem?.subscription_plan_id,
    ) ?? subItem?.subscription_plans?.[0];

  const r0 = cart.recurring?.[0];

  // Interval + unit: prefer cart.recurring[0], then chosen plan, then item
  const intervalCount =
    Number(
      r0?.interval ??
        chosenPlan?.billing_interval ??
        subItem?.subscription_interval ??
        1,
    ) || 1;

  const intervalUnit = (
    (r0?.interval_unit as string) ||
    chosenPlan?.billing_interval_unit ||
    subItem?.subscription_interval_unit ||
    "month"
  ).toLowerCase() as IntervalUnit;

  // Amount (prefer recurring subtotal if present)
  const parsedItemPrice = parseFloat(
    subItem?.subscription_price ?? subItem?.price ?? "",
  );
  const rawAmount = Number(
    r0?.subtotal ??
      (!isNaN(parsedItemPrice) ? parsedItemPrice : cart.amount_total),
  );
  const amount = toPaymentAmount(
    Number.isFinite(rawAmount) ? rawAmount : 0,
    decimalPlaces,
  );

  // Start date (prefer cart.recurring[0])
  const startDate =
    r0?.start_date ||
    chosenPlan?.subscription_start ||
    subItem?.subscription_start ||
    new Date().toISOString();

  // End date: from the *chosen plan's* max_billing_cycles
  const maxCycles = chosenPlan?.max_billing_cycles;

  let endDateISO: string | undefined;
  if (maxCycles && maxCycles > 0) {
    const start = new Date(startDate);
    const end = new Date(start);
    const c = intervalCount * maxCycles;
    switch (intervalUnit) {
      case "day":
        end.setDate(end.getDate() + c);
        break;
      case "week":
        end.setDate(end.getDate() + 7 * c);
        break;
      case "month":
        end.setMonth(end.getMonth() + c);
        break;
      case "year":
        end.setFullYear(end.getFullYear() + c);
        break;
    }
    endDateISO = end.toISOString();
  }

  const regularBilling = buildRecurringLineItem({
    label: `${subItem?.product_title || cart.company?.name || "Subscription"}`,
    amount,
    startDate,
    intervalUnit,
    intervalCount,
    endDate: endDateISO,
  });

  // Optional trial from the chosen plan
  const trialCount = Number(chosenPlan?.trial_period || 0);
  const trialUnit = String(chosenPlan?.trial_period_unit || "").toLowerCase();

  let trialBilling: ApplePayJS.ApplePayLineItem | undefined;
  if (trialCount > 0 && ["day", "week", "month", "year"].includes(trialUnit)) {
    trialBilling = buildRecurringLineItem({
      label: "Trial",
      amount: toPaymentAmount(0, decimalPlaces),
      startDate,
      intervalUnit: trialUnit as IntervalUnit,
      intervalCount: trialCount,
    });
  }

  const paymentDescription = `${
    subItem?.product_title || "Subscription"
  } — ${intervalCount} ${intervalUnit}${intervalCount > 1 ? "s" : ""}`;

  const recurringPaymentRequest: ApplePayJS.ApplePayRecurringPaymentRequest = {
    paymentDescription,
    regularBilling,
    ...(trialBilling ? { trialBilling } : {}),
    managementURL: "https://checkout.fluid.app/manage",
  };

  return recurringPaymentRequest;
}
